// Generative-cover entry point. Maps the v9.1 Dot Studio `state` defaults +
// `buildConfig()` (morfi-pattern-studio_9_1.html) onto a DotRenderConfig, then
// renders the final coloured cover via densityField → buildDots → drawCanvas.
//
// Colours come ONLY from DOT_PALETTES + pickColor (the HTML palette system).
// There is no mosaic, no brand-blue override layer — what buildDots colours is
// exactly what gets baked into the PNG.

import { buildDensityConfig, type DensityConfigOverrides } from './build-config';
import { densityField } from './density-field';
import { clamp } from './math';
import { DOT_PALETTES } from './palettes';
import {
  buildDots,
  type DotRenderConfig,
  drawCanvas,
  type FadeMode,
  type GridType,
  type MarkVariant,
} from './render-dots';

type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/**
 * Canvas width the HTML Dot Studio defaults were tuned against. Spacing and
 * spacing is absolute pixels — without scaling it with output width, a 1500 px
 * export is ~2.5× denser than the 600 px picker preview when both fill the
 * same 3:1 hero slot.
 */
export const COVER_LAYOUT_REFERENCE_WIDTH = 600;

/**
 * Default dot-grid knobs for generative hero covers (Morfi product defaults).
 * Image- and mosaic-source fields are omitted — generative covers only.
 */
const DOT_STATE_DEFAULTS = {
  spacing: 11,
  minDot: 2,
  maxDot: 76,
  intensity: 0.92,
  element: 'slasho',
  gridAngle: 45,
  coverage: 0.12,
  falloff: 1.35,
  baseFill: 0.85,
  gridType: 'square' as GridType,
  mark: 'off',
  markShape: 'ring',
  markLayout: 'trio',
  markPolarity: 'positive',
  markSize: 0.5,
  markThick: 0.55,
  markStrength: 0.3,
  backgroundMode: 'solid',
  fadeMode: 'none' as FadeMode,
  fadeSize: 0.16,
  fadeRoughness: 0.42,
  shine: false,
  shineAngle: 135,
  markVariant: 'axis' as MarkVariant,
} as const;

/** Render knobs that can be tuned per cover. `minDot`/`maxDot` are 0–100 (HTML slider scale). */
export type DotCoverOverrides = DensityConfigOverrides &
  Partial<{
    spacing: number;
    minDot: number;
    maxDot: number;
    intensity: number;
    element: string;
    gridAngle: number;
    coverage: number;
    falloff: number;
    baseFill: number;
    gridType: GridType;
    backgroundMode: string;
    fadeMode: FadeMode;
    fadeSize: number;
    shine: boolean;
    shineAngle: number;
    markVariant: MarkVariant;
    colors: readonly [string, string, string, string];
    background: string;
    strokeWeight: number;
  }>;

/**
 * Build the full engine config for a generative cover. Verbatim mapping of the
 * HTML `buildConfig()`: dot percentages → fractions, `falloff` → `shapePow`,
 * brand palette → colors + background, breakthrough kaleN clamped to 6..12.
 */
export function buildDotRenderConfig(
  mode: string,
  seed: number | string,
  width: number,
  height: number,
  overrides: DotCoverOverrides = {},
): DotRenderConfig {
  const density = buildDensityConfig(mode, seed, width, height, overrides);
  const palette = DOT_PALETTES.brand;

  const minDot = overrides.minDot ?? DOT_STATE_DEFAULTS.minDot;
  const maxDot = overrides.maxDot ?? DOT_STATE_DEFAULTS.maxDot;
  const kaleN = mode === 'breakthrough' ? clamp(density.kaleN || 0, 6, 12) : density.kaleN;
  const layoutScale = width / COVER_LAYOUT_REFERENCE_WIDTH;
  const baseSpacing = overrides.spacing ?? DOT_STATE_DEFAULTS.spacing;

  return {
    ...density,
    kaleN,
    spacing: baseSpacing * layoutScale,
    minFrac: Math.min(1, minDot / 100),
    maxFrac: Math.min(0.85, maxDot / 100),
    intensity: overrides.intensity ?? DOT_STATE_DEFAULTS.intensity,
    shape: overrides.element ?? DOT_STATE_DEFAULTS.element,
    seamless: true,
    shapePow: overrides.falloff ?? DOT_STATE_DEFAULTS.falloff,
    coverage: overrides.coverage ?? DOT_STATE_DEFAULTS.coverage,
    baseFill: overrides.baseFill ?? DOT_STATE_DEFAULTS.baseFill,
    gridType: overrides.gridType ?? DOT_STATE_DEFAULTS.gridType,
    gridAngle: overrides.gridAngle ?? DOT_STATE_DEFAULTS.gridAngle,
    mark: DOT_STATE_DEFAULTS.mark,
    markShape: DOT_STATE_DEFAULTS.markShape,
    markLayout: DOT_STATE_DEFAULTS.markLayout,
    markPolarity: DOT_STATE_DEFAULTS.markPolarity,
    markSize: DOT_STATE_DEFAULTS.markSize,
    markThick: DOT_STATE_DEFAULTS.markThick,
    markStrength: DOT_STATE_DEFAULTS.markStrength,
    markMask: null,
    markVariant: overrides.markVariant ?? DOT_STATE_DEFAULTS.markVariant,
    colors: overrides.colors ?? palette.colors,
    background: overrides.background ?? palette.background,
    backgroundMode: overrides.backgroundMode ?? DOT_STATE_DEFAULTS.backgroundMode,
    fadeMode: overrides.fadeMode ?? DOT_STATE_DEFAULTS.fadeMode,
    fadeSize: overrides.fadeSize ?? DOT_STATE_DEFAULTS.fadeSize,
    fadeRoughness: DOT_STATE_DEFAULTS.fadeRoughness,
    shine: overrides.shine ?? DOT_STATE_DEFAULTS.shine,
    shineAngle: overrides.shineAngle ?? DOT_STATE_DEFAULTS.shineAngle,
    ...(overrides.strokeWeight !== undefined ? { strokeWeight: overrides.strokeWeight } : {}),
    colorAt: null,
  };
}

/** Render a finished, coloured cover onto a 2D context (preview == export). */
export function renderDotStudioCover(ctx: Ctx2D, cfg: DotRenderConfig): void {
  const dots = buildDots(cfg, (x, y) => densityField(x, y, cfg));
  drawCanvas(ctx, dots, cfg);
}
